/**
 * The Form Order dialog's draft: what it numbers, whether the admin changed anything, and what it
 * sends. See Form order and Entry rules in CONTEXT.md.
 *
 * Kept out of the dialog so it can be tested without mounting one. The dialog keeps its own copy of
 * the rows and hands them in; nothing here writes to them.
 */
import { isEqual } from 'lodash';
import type { FormEnforcements, FormEnforcementsPatch, FormModel, StudyFormEnforcementsPatch } from '@/models';

/** The four switches a row edits. `closeEntry` is absent on a form that never had it, so read it as off. */
const entryRules = (enforcements: FormEnforcements) => [
  enforcements.enforceOrder,
  enforcements.onlyOnce,
  enforcements.hidden,
  !!enforcements.closeEntry
];

/** A row with no links stores no `links` key, and one whose last link was removed loses the key. */
const storedLinks = (enforcements: FormEnforcements): unknown[] => enforcements.links ?? [];

const sameRow = (opened: FormModel, draft: FormModel) =>
  opened.identifier === draft.identifier &&
  isEqual(entryRules(opened.enforcements), entryRules(draft.enforcements)) &&
  isEqual(storedLinks(opened.enforcements), storedLinks(draft.enforcements));

/**
 * Each row's identifier and enforcements, with its place set to its row, counting from 1. A shared
 * form sends its place alone: its entry rules and form links are the owning study's, and this study
 * stores only where the form sits in its own form order.
 *
 * Every row gets a place, whatever it arrived with: a new form comes back with none, a clone with
 * its source's, and a shared form this study never placed with `null`, so the numbers on the list
 * cannot be trusted. Placing every shared form is what stops the owning study's reordering from
 * moving it here after the first save (docs/forms/REVIEW.md F4, decision 52).
 */
export const numberFormOrder = (forms: FormModel[]): FormEnforcementsPatch[] =>
  forms.map((form, index) => ({
    formIdentifier: form.identifier,
    enforcements: form.sharedForm ? { order: index + 1 } : { ...form.enforcements, order: index + 1 }
  }));

/**
 * Whether the draft differs from the rows the dialog opened on, in the sequence of the rows, their
 * entry rules or their form links. The place a row arrived with is not compared: the admin sees only
 * the sequence, and a save numbers every row again anyway.
 */
export const formOrderChanged = (opened: FormModel[], draft: FormModel[]): boolean =>
  opened.length !== draft.length || opened.some((form, index) => !sameRow(form, draft[index]));

/**
 * The body of `PATCH study/:id/form-enforcements` for the draft. An own form's enforcements go whole,
 * keys this dialog does not edit included, because the endpoint replaces them rather than merging.
 */
export const formOrderPayload = (forms: FormModel[]): StudyFormEnforcementsPatch => ({
  forms: numberFormOrder(forms)
});
